import { message } from 'antd'
import { chooseDocumentFileRef } from 'features/files/hooks/useDocuments'
import { RolodexDocumentEntry } from 'features/files/types'
import { getDocumentDownloadName } from 'features/files/utils/documentDownloadName'
import {
  useFileSignedUrlFetcher,
  useGetRolodexFileLSFetcher,
} from 'features/rolodex'
import { useCallback, useState } from 'react'
import { downloadFromUrl } from 'utils/download'

export type DownloadableDocument = Pick<
  RolodexDocumentEntry,
  'entry_id' | 'filename' | 'visible_name' | 'file_type'
>

/**
 * Downloads a document entry's backing file to the user's machine. Resolves
 * the blob path and signed URL lazily on each call, so it is safe to use from
 * lists without fetching URLs for every row.
 */
export const useDocumentDownload = (): {
  downloadDocument: (document: DownloadableDocument) => Promise<void>
  downloadingIds: ReadonlySet<string>
} => {
  const listFiles = useGetRolodexFileLSFetcher()
  const { fetchSignedUrl } = useFileSignedUrlFetcher()
  const [downloadingIds, setDownloadingIds] = useState<ReadonlySet<string>>(
    new Set()
  )

  const downloadDocument = useCallback(
    async (document: DownloadableDocument) => {
      setDownloadingIds((prev) => new Set(prev).add(document.entry_id))
      try {
        const fileRefs = await listFiles({
          entry_id: document.entry_id,
          path: '**',
        })
        const fileRef = chooseDocumentFileRef(fileRefs, document)
        if (!fileRef) {
          message.warning('This document has no file attached to download.')
          return
        }

        const result = await fetchSignedUrl({
          queryOrQueries: [{ entry_id: fileRef.entry_id, path: fileRef.path }],
        })
        const signedUrl = result?.url
        if (!signedUrl) {
          throw new Error('Could not get a download URL for this document')
        }

        await downloadFromUrl(
          signedUrl,
          getDocumentDownloadName(document, fileRef.path)
        )
      } catch {
        message.error('Failed to download document. Please try again.')
      } finally {
        setDownloadingIds((prev) => {
          const next = new Set(prev)
          next.delete(document.entry_id)
          return next
        })
      }
    },
    [listFiles, fetchSignedUrl]
  )

  return { downloadDocument, downloadingIds }
}
